import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  Edit3,
  Loader2,
  AlertCircle,
  X,
  Download,
  User,
  Check,
  ChevronRight,
  ChevronLeft,
  Activity,
  Mail,
} from "lucide-react";
import {
  listClients,
  createClient,
  updateClient,
  sendClientInvite,
  deleteClient,
  getClientLogs,
  exportClientCsvUrl,
  adminCreateDaily,
  adminCreateWeekly,
  adminCreateMonthly,
} from "../lib/api";
import LineChart from "./LineChart";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  pin: "",
  height_cm: "",
  is_t1d: false,
  notes: "",
  send_invite: true,
};

export default function AdminClientsPanel({ pin }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // client id
  const [editing, setEditing] = useState(null); // client or {} for new
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null); // {ok, text}
  const [inviting, setInviting] = useState(null);

  const invite = async (c) => {
    setInviting(c.id);
    try {
      await sendClientInvite(pin, c.id);
      setNotice({ ok: true, text: `Install invite emailed to ${c.email}` });
      await load();
    } catch (err) {
      setNotice({ ok: false, text: err?.response?.data?.detail || "Could not send invite" });
    } finally {
      setInviting(null);
    }
  };
  const load = async () => {
    setLoading(true);
    try {
      const c = await listClients(pin);
      setClients(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => setEditing({ ...EMPTY_FORM, _new: true });
  const openEdit = (c) => setEditing({ ...c, pin: "", _new: false });

  const saveClient = async (form) => {
    setError("");
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        is_t1d: !!form.is_t1d,
        notes: form.notes || null,
      };
      if (form._new) {
        if (!form.pin || form.pin.length < 4) {
          setError("PIN must be at least 4 digits");
          return;
        }
        const created = await createClient(pin, { ...payload, pin: form.pin });
        if (form.send_invite && created.email) {
          setEditing(null);
          await invite(created);
          return;
        }
      } else {
        if (form.pin) payload.pin = form.pin;
        await updateClient(pin, form.id, payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save");
    }
  };

  const removeClient = async (id) => {
    if (!window.confirm("Delete this client and all their logs? This cannot be undone.")) return;
    await deleteClient(pin, id);
    if (selected === id) setSelected(null);
    await load();
  };

  if (selected) {
    return (
      <ClientDetail
        pin={pin}
        clientId={selected}
        onBack={() => setSelected(null)}
        onEdit={(c) => openEdit(c)}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm text-[var(--lc-muted)]">
          {clients.length} active {clients.length === 1 ? "client" : "clients"}
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--lc-moss)] hover:bg-[var(--lc-moss-dark)] text-white text-[11px] font-bold tracking-[0.18em] uppercase"
        >
          <Plus className="h-3.5 w-3.5" /> Add Client
        </button>
      </div>

      {notice && (
        <div
          className={`mb-4 flex items-start justify-between gap-3 px-4 py-3 text-sm rounded-sm border ${
            notice.ok ? "bg-[var(--lc-cream-soft)] border-[var(--lc-moss)] text-[var(--lc-moss-dark)]" : "bg-red-50 border-red-200 text-red-700"
          }`}
          data-testid="client-invite-notice"
        >
          <span className="flex items-center gap-2">
            {notice.ok ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {notice.text}
          </span>
          <button onClick={() => setNotice(null)} data-testid="client-invite-notice-close">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--lc-muted)]" />
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-12 text-center">
          <User className="h-8 w-8 mx-auto text-[var(--lc-muted)] mb-3" />
          <div className="font-serif-display text-xl">No clients yet</div>
          <p className="text-sm text-[var(--lc-muted)] mt-1">
            Add your first client to start tracking their progress.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {clients.map((c) => (
            <article
              key={c.id}
              className="bg-white border border-[var(--lc-line)] rounded-md p-4 lg:p-5 flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-full bg-[var(--lc-moss)] grid place-items-center font-serif-display text-base text-white shrink-0">
                {c.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-serif-display text-xl leading-tight">{c.name}</h3>
                  {c.is_t1d && (
                    <span className="text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-0.5 bg-amber-500/15 text-amber-800 rounded-full">
                      T1D
                    </span>
                  )}
                  {!c.active && (
                    <span className="text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-0.5 bg-neutral-200 text-neutral-600 rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--lc-muted)] mt-0.5 truncate">
                  {c.email || c.phone || "—"}
                  {c.invite_sent_at && (
                    <span className="ml-2 text-[var(--lc-moss)]" data-testid={`client-invited-${c.id}`}>
                      · App invite sent {new Date(c.invite_sent_at + (c.invite_sent_at.endsWith("Z") ? "" : "Z")).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {c.email && (
                  <button
                    onClick={() => invite(c)}
                    disabled={inviting === c.id}
                    className="p-2 text-[var(--lc-muted)] hover:text-[var(--lc-moss)] disabled:opacity-50"
                    title={c.invite_sent_at ? "Resend app invite" : "Email app invite"}
                    data-testid={`client-invite-btn-${c.id}`}
                  >
                    {inviting === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  </button>
                )}
                <button
                  onClick={() => setSelected(c.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold tracking-[0.14em] uppercase bg-[var(--lc-ink)] text-[var(--lc-cream)] hover:bg-[var(--lc-moss-dark)]"
                >
                  <Activity className="h-3.5 w-3.5" />
                  View
                </button>
                <button
                  onClick={() => openEdit(c)}
                  className="p-2 text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"
                  title="Edit"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => removeClient(c.id)}
                  className="p-2 text-[var(--lc-muted)] hover:text-red-700"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <ClientFormModal
          initial={editing}
          error={error}
          onSave={saveClient}
          onClose={() => {
            setEditing(null);
            setError("");
          }}
        />
      )}
    </div>
  );
}

function ClientFormModal({ initial, onSave, onClose, error }) {
  const [form, setForm] = useState(initial);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--lc-line)]">
          <h3 className="font-serif-display text-2xl">
            {form._new ? "Add" : "Edit"} <span className="italic">client</span>
          </h3>
          <button onClick={onClose} className="p-2 -mr-2 hover:opacity-70">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="eyebrow text-[var(--lc-muted)] mb-2 block">Full name *</label>
            <input className="w-full px-4 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm" value={form.name} onChange={(e) => update("name", e.target.value)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="eyebrow text-[var(--lc-muted)] mb-2 block">Email</label>
              <input type="email" className="w-full px-4 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm" value={form.email || ""} onChange={(e) => update("email", e.target.value)} />
            </div>
            <div>
              <label className="eyebrow text-[var(--lc-muted)] mb-2 block">Phone</label>
              <input className="w-full px-4 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm" value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="eyebrow text-[var(--lc-muted)] mb-2 block">
                {form._new ? "4-digit PIN *" : "New PIN (leave blank to keep current)"}
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="w-full px-4 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm tracking-[0.3em]"
                value={form.pin || ""}
                onChange={(e) => update("pin", e.target.value.replace(/\D/g, ""))}
              />
            </div>
            <div>
              <label className="eyebrow text-[var(--lc-muted)] mb-2 block">Height (cm)</label>
              <input className="w-full px-4 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm" value={form.height_cm || ""} onChange={(e) => update("height_cm", e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.is_t1d} onChange={(e) => update("is_t1d", e.target.checked)} />
            <span className="text-sm">Client has Type 1 Diabetes (shows insulin field)</span>
          </label>
          {form._new && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.send_invite && !!form.email}
                disabled={!form.email}
                onChange={(e) => update("send_invite", e.target.checked)}
                data-testid="client-form-send-invite"
              />
              <span className={`text-sm ${form.email ? "" : "text-[var(--lc-muted)]"}`}>
                Email a one-tap app install link {form.email ? "" : "(add an email first)"}
              </span>
            </label>
          )}
          <div>
            <label className="eyebrow text-[var(--lc-muted)] mb-2 block">Internal notes</label>
            <textarea rows={3} className="w-full px-4 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm resize-none" value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} />
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[var(--lc-line)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2.5 bg-white border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.18em] uppercase">
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            className="px-5 py-2.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[11px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)]"
          >
            {form._new ? "Create Client" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------- Client detail (admin view with charts) -------- */
function ClientDetail({ pin, clientId, onBack, onEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getClientLogs(pin, clientId, 180);
      setData(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (loading)
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--lc-muted)]" />
      </div>
    );
  if (!data) return null;

  const { client, daily, weekly, monthly } = data;

  const daily_asc = daily.slice().reverse();
  const weekly_asc = weekly.slice().reverse();
  const monthly_asc = monthly.slice().reverse();

  const totalWater = daily.reduce((s, d) => s + (d.water_ml || 0), 0);
  const totalSteps = daily.reduce((s, d) => s + (d.steps || 0), 0);
  const fastingDays = daily.filter((d) => d.fasting).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold tracking-[0.18em] uppercase bg-white border border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Back to Clients
        </button>
        <div className="flex items-center gap-2">
          <a
            href={exportClientCsvUrl(clientId) + `?x-admin-pin=${encodeURIComponent(pin)}`}
            onClick={async (e) => {
              // We need to send pin header; browser can't on <a>. Fallback: fetch blob.
              e.preventDefault();
              const url = exportClientCsvUrl(clientId);
              const res = await fetch(url, { headers: { "x-admin-pin": pin } });
              const blob = await res.blob();
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `longev-${client.name.replace(/\s+/g, "-").toLowerCase()}-daily.csv`;
              link.click();
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold tracking-[0.18em] uppercase bg-white border border-[var(--lc-line)] hover:border-[var(--lc-ink)] cursor-pointer"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </a>
          <button
            onClick={() => onEdit(client)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold tracking-[0.18em] uppercase bg-[var(--lc-ink)] text-[var(--lc-cream)] hover:bg-[var(--lc-moss-dark)]"
          >
            <Edit3 className="h-3.5 w-3.5" /> Edit Client
          </button>
        </div>
      </div>

      <div className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6 mb-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[var(--lc-moss)] grid place-items-center font-serif-display text-lg text-white">
            {client.name[0]}
          </div>
          <div>
            <h2 className="font-serif-display text-2xl leading-tight">{client.name}</h2>
            <div className="text-xs text-[var(--lc-muted)] mt-0.5">
              {client.email && <>{client.email} · </>}
              {client.phone && <>{client.phone} · </>}
              {client.height_cm && <>{client.height_cm} cm</>}
            </div>
            {client.is_t1d && (
              <span className="inline-block mt-2 text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-0.5 bg-amber-500/15 text-amber-800 rounded-full">
                T1D
              </span>
            )}
          </div>
        </div>
        {client.notes && (
          <p className="mt-4 text-sm bg-[var(--lc-cream-soft)] border border-[var(--lc-line)] rounded-sm p-3">
            {client.notes}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Kpi label="Days logged" value={daily.length} />
        <Kpi label="Total steps" value={totalSteps.toLocaleString()} />
        <Kpi label="Fasting days" value={fastingDays} />
        <Kpi label="Weekly logs" value={weekly.length} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Weight (kg)">
          <LineChart data={weekly_asc.map((w) => ({ x: w.week_start.slice(5), y: w.weight_kg ?? null }))} unit="kg" color="#3E5A7A" />
        </ChartCard>
        <ChartCard title="HbA1c (%)">
          <LineChart data={weekly_asc.map((w) => ({ x: w.week_start.slice(5), y: w.hba1c ?? null }))} unit="%" color="#B08585" />
        </ChartCard>
        <ChartCard title="Body fat %">
          <LineChart data={monthly_asc.map((m) => ({ x: m.month.slice(2), y: m.body_fat_pct ?? null }))} unit="%" color="#8C6A3E" />
        </ChartCard>
        <ChartCard title="Muscle mass">
          <LineChart data={monthly_asc.map((m) => ({ x: m.month.slice(2), y: m.muscle_mass_kg ?? null }))} unit="kg" color="#5C7A4F" />
        </ChartCard>
        <ChartCard title="Daily steps">
          <LineChart data={daily_asc.map((d) => ({ x: d.log_date.slice(5), y: d.steps || null }))} color="#4A6B7A" />
        </ChartCard>
        <ChartCard title="Sleep (h)">
          <LineChart data={daily_asc.map((d) => ({ x: d.log_date.slice(5), y: d.sleep_hours ?? null }))} unit="h" color="#5A5A6B" />
        </ChartCard>
      </div>

      <section className="mt-6 bg-white border border-[var(--lc-line)] rounded-md p-5">
        <h3 className="eyebrow text-[var(--lc-muted)] mb-3">Recent daily entries</h3>
        {daily.length === 0 ? (
          <p className="text-sm text-[var(--lc-muted)]">No daily logs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.16em] uppercase text-[var(--lc-muted)] border-b border-[var(--lc-line)]">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Water</th>
                  <th className="py-2 pr-3">Steps</th>
                  <th className="py-2 pr-3">Sleep</th>
                  <th className="py-2 pr-3">Fast</th>
                  {client.is_t1d && <th className="py-2 pr-3">Insulin</th>}
                  <th className="py-2 pr-3">Food</th>
                </tr>
              </thead>
              <tbody>
                {daily.slice(0, 30).map((d) => (
                  <tr key={d.id} className="border-b border-[var(--lc-line)]">
                    <td className="py-2 pr-3 font-semibold">{d.log_date}</td>
                    <td className="py-2 pr-3">{d.water_ml || "—"} ml</td>
                    <td className="py-2 pr-3">{(d.steps || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3">{d.sleep_hours ?? "—"}</td>
                    <td className="py-2 pr-3">{d.fasting ? "Yes" : "—"}</td>
                    {client.is_t1d && <td className="py-2 pr-3">{d.insulin_dose ?? "—"}</td>}
                    <td className="py-2 pr-3 text-xs text-[var(--lc-muted)] truncate max-w-[260px]">
                      {(d.food || []).map((f) => `${f.meal}: ${f.text}`).join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-md p-4">
      <div className="eyebrow text-[var(--lc-muted)]">{label}</div>
      <div className="font-serif-display text-2xl mt-1 leading-none">{value}</div>
    </div>
  );
}
function ChartCard({ title, children }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-md p-5">
      <h4 className="eyebrow text-[var(--lc-muted)] mb-3">{title}</h4>
      {children}
    </div>
  );
}
